import React, { useState, useMemo, useEffect } from 'react';
import { 
    Stack, 
    Typography,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { differenceInDays, differenceInWeeks, differenceInMonths, differenceInQuarters, subDays, addDays, subMonths, startOfWeek, startOfMonth, startOfQuarter, startOfDay } from 'date-fns';
import { CHART_VIEWS } from './ItemAnalytics';

const MAX_PERIODS_IN_RANGE = 128;
const MIN_PERIODS_IN_RANGE = 4;
const MIN_DAYS_IN_RANGE = 7;

function TimeRangeControls({ periodIn, onPeriodChange, dateRangeIn, onDateRangeChange, chartView }) {

    const [period, setPeriod] = useState(periodIn || 'month');
    const [startDate, setStartDate] = useState(() => {
        if (dateRangeIn?.startDate) {
            return dateRangeIn.startDate;
        }
        const today = new Date();
        const year = subMonths(today, 12).getFullYear();
        return `${year}-01-01`;
    });
    const [endDate, setEndDate] = useState(() => {
        if (dateRangeIn?.endDate) {
            return dateRangeIn.endDate;
        }
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });

    // Sync internal state with dateRangeIn prop changes
    useEffect(() => {
        handleStartDateChange(dateRangeIn?.startDate);
        handleEndDateChange(dateRangeIn?.endDate);
    }, [dateRangeIn]);

    // Check if we're in forecast mode
    const isForecastView = chartView === CHART_VIEWS.FORECAST_VIEW;
    
    // Calculate forecast date range (9 months ago to 3 months in future)
    const forecastDateRange = useMemo(() => {
        const now = new Date();
        // Start: 9 months ago at start of month
        const startDate = new Date(now.getFullYear(), now.getMonth() - 9, 1);
        // End: 3 months in future at end of month
        const endDate = new Date(now.getFullYear(), now.getMonth() + 4, 0); // 0th day = last day of previous month
        
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    }, []);

    // Parse dates for calculations
    const parseLocalDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed
    };

    const startDateObj = useMemo(() => parseLocalDate(startDate), [startDate]);
    const endDateObj = useMemo(() => parseLocalDate(endDate), [endDate]);

    // Calculate aligned date range based on selected dates and period
    const dateRange = useMemo(() => {
        // If in forecast view, return forecast range with weekly period
        if (isForecastView) {
            return {
                ...forecastDateRange,
                period: 'week'
            };
        }
        
        // Otherwise use normal date range calculation
        let alignedStart = new Date(startDateObj);
        let alignedEnd = new Date(endDateObj);
        
        // Align start date to period boundary
        switch (period) {
        case 'week':
            alignedStart = startOfWeek(alignedStart, { weekStartsOn: 1 }); // Monday start
            break;
        case 'month':
            alignedStart = startOfMonth(alignedStart);
            break;
        case 'quarter':
            alignedStart = startOfQuarter(alignedStart);
            break;
        case 'day':
        default:
            alignedStart = startOfDay(alignedStart);
            break;
        }
        
        // Format dates as strings for API
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            startDate: formatDate(alignedStart),
            endDate: formatDate(alignedEnd)
        };
    }, [startDateObj, endDateObj, period, isForecastView]);

    // Notify parent component when dateRange changes
    useEffect(() => {
        if (onDateRangeChange) {
            onDateRangeChange(dateRange);
        }
    }, [dateRange, onDateRangeChange]);
    useEffect(() => {
        const effectivePeriod = isForecastView ? 'week' : period;
        if (onPeriodChange) {
            onPeriodChange(effectivePeriod);
        }
    }, [period, onPeriodChange, isForecastView]);

    // Calculate available periods based on date range
    const availablePeriodsWithCounts = useMemo(() => {
        const dayDiff = Math.max(1, differenceInDays(endDateObj, startDateObj) + 1);
        const weekDiff = Math.max(1, differenceInWeeks(endDateObj, startDateObj) + 1);
        const monthDiff = Math.max(1, differenceInMonths(endDateObj, startDateObj) + 1);
        const quarterDiff = Math.max(1, differenceInQuarters(endDateObj, startDateObj) + 1);

        return [
            { value: 'day', label: 'Daily', count: dayDiff, minDays: 1 },
            { value: 'week', label: 'Weekly', count: weekDiff, minDays: 7 },
            { value: 'month', label: 'Monthly', count: monthDiff, minDays: 28 },
            { value: 'quarter', label: 'Quarterly', count: quarterDiff, minDays: 84 }
        ];
    }, [startDateObj, endDateObj]);

    const optimalPeriod = useMemo(() => {
        const validPeriods = availablePeriodsWithCounts
            .filter(p => p.count <= MAX_PERIODS_IN_RANGE && p.count >= MIN_PERIODS_IN_RANGE)
            .sort((a, b) => b.minDays - a.minDays);

        return validPeriods.length > 0 ? validPeriods[0].value : 'day';
    }, [availablePeriodsWithCounts, startDateObj, endDateObj]);

    // Auto-adjust period if current selection is invalid
    useEffect(() => {
        if (optimalPeriod && period !== optimalPeriod) {
            const currentPeriodData = availablePeriodsWithCounts.find(p => p.value === period);
            if (!currentPeriodData || currentPeriodData.count > MAX_PERIODS_IN_RANGE || currentPeriodData.count < MIN_PERIODS_IN_RANGE) {
                setPeriod(optimalPeriod);
                if (onPeriodChange) {
                    onPeriodChange(optimalPeriod);
                }
            }
        }
    }, [optimalPeriod, period, onPeriodChange, availablePeriodsWithCounts]);

    const handleStartDateChange = (newDate) => {
        if (!newDate) return;
        
        const daysDiff = differenceInDays(endDateObj, newDate) + 1;
        
        if (daysDiff >= MIN_DAYS_IN_RANGE) {
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            setStartDate(formatDate(newDate));
        }
    };

    const handleEndDateChange = (newDate) => {
        if (!newDate) return;
        
        const daysDiff = differenceInDays(newDate, startDateObj) + 1;
        
        if (daysDiff >= MIN_DAYS_IN_RANGE) {
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            setEndDate(formatDate(newDate));
        }
    };

    const handlePeriodChange = (event) => {
        const newPeriod = event.target.value;
        setPeriod(newPeriod);
        if (onPeriodChange) {
            onPeriodChange(newPeriod);
        }
    };

    const availablePeriods = availablePeriodsWithCounts.filter(p => p.count <= MAX_PERIODS_IN_RANGE && p.count >= MIN_PERIODS_IN_RANGE);

    // Calculate date constraints for visual feedback
    const dateConstraints = useMemo(() => {
        // Get current date in Chicago timezone
        const getChicagoDate = () => {
            const now = new Date();
            const chicagoTime = now.toLocaleString('en-US', { timeZone: 'America/Chicago' });
            return new Date(chicagoTime);
        };
        
        let today = getChicagoDate();
        today.setDate(1);
        today.setUTCMinutes(0);
        today.setUTCHours(0);
        today.setUTCSeconds(0);
        today.setUTCMilliseconds(0);
        today.setFullYear(today.getFullYear() - 1);
        const oneYearAgo = new Date(today);
        today = getChicagoDate(); // Reset to current Chicago date
        
        const startDateMax = subDays(endDateObj, MIN_DAYS_IN_RANGE);
        const endDateMin = addDays(startDateObj, MIN_DAYS_IN_RANGE);
        
        return {
            startDateMax,
            endDateMin,
            maxDate: today, // Can't select future dates
            minDate: oneYearAgo // Can't select dates older than 1 year
        };
    }, [startDateObj, endDateObj]);

    // Disable date functions for DatePicker
    const shouldDisableStartDate = (date) => {
        return date > dateConstraints.startDateMax || 
               date > dateConstraints.maxDate;
    };

    const shouldDisableEndDate = (date) => {
        return date < dateConstraints.endDateMin || 
               date > dateConstraints.maxDate;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {!isForecastView && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-start">
                    <Stack spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Date Range
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <DatePicker
                                label="Start Date"
                                value={startDateObj}
                                onChange={handleStartDateChange}
                                shouldDisableDate={shouldDisableStartDate}
                                slotProps={{
                                    textField: {
                                        size: 'small'
                                    }
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDateObj}
                                onChange={handleEndDateChange}
                                shouldDisableDate={shouldDisableEndDate}
                                slotProps={{
                                    textField: {
                                        size: 'small'
                                    }
                                }}
                            />
                        </Stack>
                    </Stack>
                    
                    <Stack spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Period
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={period}
                                onChange={handlePeriodChange}
                            >
                                {availablePeriods.map((periodOption) => (
                                    <MenuItem key={periodOption.value} value={periodOption.value}>
                                        {periodOption.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            )}
        </LocalizationProvider>
    );
}

export default TimeRangeControls;